// referral-cookie.ts — client-side handler for the `wwaave_ref_share` cookie.
//
// North Star §7 wire-up (T5). The marketing landing page harvests `?r=<slug>`
// from the URL and writes the slug into a 60-day cookie. The sign-up form
// reads the cookie (and the in-URL `?ref=` param the existing /sign-up route
// already accepts) and submits the slug to the worker as `ref_artist_slug`.
//
// Why not HttpOnly: the SPA needs to read this to populate the sign-up body.
// The cookie holds an artist slug (public information — visible on every
// /artists/:slug page) so the disclosure value is nil; the worker also
// re-validates the slug shape AND re-resolves it through artist_profiles
// before stamping any attribution.
//
// SameSite=Lax matches the rest of the cookie set (session/CSRF/hat). Secure
// is set in production; in dev (http://localhost:5173) it gets dropped at
// write time because Chrome will reject Secure cookies on insecure origins.

export const REFERRAL_COOKIE_NAME = 'wwaave_ref_share';
const REFERRAL_COOKIE_MAX_AGE_SEC = 60 * 24 * 60 * 60; // 60 days

/**
 * Artist-slug shape. Mirrors the worker's `REF_SLUG_PATTERN` (auth.ts:61) and
 * the sign-up search validator (`REF_SLUG_RE`, sign-up.tsx). Keep all three in
 * lockstep when the slug shape changes.
 */
export const REFERRAL_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/**
 * Persist a referral slug into the share cookie. Caller is responsible for
 * validating the slug shape — `setReferralCookie('not a slug')` will round-trip
 * verbatim and the worker will reject it on sign-up.
 *
 * SSR-safe — no-ops in non-browser contexts.
 */
export function setReferralCookie(slug: string): void {
  if (typeof document === 'undefined') return;
  const value = encodeURIComponent(slug);
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const securePart = isHttps ? '; Secure' : '';
  document.cookie = `${REFERRAL_COOKIE_NAME}=${value}; Path=/; Max-Age=${REFERRAL_COOKIE_MAX_AGE_SEC}; SameSite=Lax${securePart}`;
}

/**
 * Read the current referral slug from the cookie, or null if absent / malformed.
 * Re-validates the shape so a tampered cookie can't pass through to the API
 * (the worker re-validates anyway; this is belt-and-braces + a faster fail).
 */
export function readReferralCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${REFERRAL_COOKIE_NAME}=([^;]+)`));
  if (!m) return null;
  const raw = decodeURIComponent(m[1] ?? '').toLowerCase().trim();
  return REFERRAL_SLUG_PATTERN.test(raw) ? raw : null;
}

/**
 * Clear the cookie. Used by the sign-up handler on successful submission so
 * subsequent loads don't keep re-claiming attribution (the worker-side
 * `REF_COOKIE` already carries forward into the add-capability flow).
 */
export function clearReferralCookie(): void {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const securePart = isHttps ? '; Secure' : '';
  document.cookie = `${REFERRAL_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${securePart}`;
}
